import cx from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { useCustomAuth } from '@/contexts/AuthContext';
import { isDesktop } from 'react-device-detect';

import { CustomButton } from '@/components/CustomButton';
import { Tag } from '@/components/Tag';
import { fonts } from '@/constants/fonts';
import { useTimeAgo } from '@/hooks/useTimeAgo';
import commentIcon from '@/svgs/comment.svg';
import heartIcon from '@/svgs/heart.svg';
import { MediaUrls, getMediaUrls } from '@/utils/getMediaUrls';

import { CustomIcon } from '@/components/CustomIcon';
import { faVideoSlash } from '@fortawesome/free-solid-svg-icons';

import styles from '../PreviewCard.module.scss';
import { useVideoPreview } from '../hooks/useVideoPreview';
import { EAspect, ECardSizes, IItemCardProps } from '../PreviewCard.types';

export const ItemCard: React.FC<IItemCardProps> = ({
  data,
  size = ECardSizes.S,
  innerRef,
  playlistId = null,
  searchString = null,
  collectionId = null,
  isHome = false,
  cardTitle,
  showCreatedTime = true,
  showMetadata = true,
  priority = false,
  previewSource,
}) => {
  const router = useRouter();
  const { isAuthenticated } = useCustomAuth();
  const { posterUrl, posterLandscapeUrl, videoUrl } =
    (getMediaUrls(data?.mediaEndpoints) as MediaUrls) || {};

  const query = useMemo(
    () => ({
      ...(playlistId ? { c: playlistId } : {}),
      ...(searchString ? { q: searchString } : {}),
      ...(collectionId ? { f: collectionId } : {}),
    }),
    [playlistId, searchString, collectionId],
  );

  const idToSearch = useMemo(
    () => (data?.sanitizedTitle !== '' ? data?.sanitizedTitle : data?.id),
    [data],
  );

  const urlForButton = `/feed/${idToSearch}`;

  const createdFormattedTime = useTimeAgo(data?.created);

  const showBannerCard =
    size === ECardSizes.L || (isDesktop && size === ECardSizes.M);

  const isPreviewEnabled = isDesktop && size !== ECardSizes.L && !!videoUrl;

  const { videoRef, handleMouseEnter, handleMouseLeave, isHovering } =
    useVideoPreview(videoUrl || '', isPreviewEnabled, {
      id: data?.id ?? '',
      title: data?.title ?? '',
      categories: data?.categories,
      source: previewSource,
      isAuthenticated,
    });

  const isVideoAvailable = !!data?.id;

  return (
    <Link
      href={{
        pathname: `/feed/${idToSearch}`,
        query,
      }}
      prefetch={false}
      className={cx(styles.anchor, {
        [styles['video-unavailable']]: !isVideoAvailable,
      })}
    >
      <div
        className={cx(
          styles['preview-card'],
          styles[EAspect.DEFAULT],
          styles[size],
          { [styles['with-preview']]: isPreviewEnabled },
        )}
        ref={innerRef}
      >
        {!isVideoAvailable ? (
          <div>
            <div className={styles.thumb}>
              <CustomIcon icon={faVideoSlash} className={styles.icon} />
            </div>
            <div
              className={cx(
                styles['title-container'],
                styles['title-default'],
                styles['video-unavailable'],
              )}
            >
              <span className={styles.title}>Video no disponible</span>
            </div>
          </div>
        ) : (
          <>
            <div
              className={styles.thumb}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {(() => {
                const imageSrc = showBannerCard
                  ? posterLandscapeUrl
                  : posterUrl;
                return (
                  imageSrc && (
                    <img
                      src={imageSrc}
                      alt={data.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                      }}
                      loading={priority ? 'eager' : 'lazy'}
                      // @ts-ignore
                      fetchPriority={priority ? 'high' : 'auto'}
                    />
                  )
                );
              })()}
              {isPreviewEnabled && (
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  preload="none"
                  className={cx(styles['video-preview'], {
                    [styles['video-preview--visible']]: isHovering,
                  })}
                />
              )}
            </div>
            <div className={styles.data}>
              {showBannerCard && (
                <>
                  <Tag>{cardTitle ?? 'Destacado'}</Tag>
                  <div
                    className={cx(
                      styles['title-container'],
                      styles['title-large'],
                      fonts.barlow.className,
                    )}
                  >
                    {isHome ? (
                      <h3 className={styles.title}>{data.title}</h3>
                    ) : (
                      <h2 className={styles.title}>{data.title}</h2>
                    )}
                  </div>
                </>
              )}
              <div className={styles.specs}>
                {showCreatedTime && (
                  <div
                    className={styles['specs-item']}
                    suppressHydrationWarning
                  >
                    hace {createdFormattedTime}
                  </div>
                )}
                {showMetadata && (
                  <>
                    <div className={styles['specs-item']}>
                      <img
                        alt="comments"
                        src={commentIcon.src}
                        width={16}
                        height={16}
                      />
                      {data?.commentsCount}
                    </div>
                    <div className={styles['specs-item']}>
                      <img
                        alt="likes"
                        src={heartIcon.src}
                        width={16}
                        height={16}
                      />
                      {data?.likesCount}
                    </div>
                  </>
                )}
              </div>
              {showBannerCard && size === ECardSizes.L && isDesktop && (
                <CustomButton
                  onClick={(e) => {
                    e.preventDefault();
                    router.push(urlForButton);
                  }}
                  title={data.title}
                >
                  Ver Video
                </CustomButton>
              )}
              {!showBannerCard && (
                <div
                  className={cx(
                    styles['title-container'],
                    styles['title-default'],
                  )}
                >
                  {isHome ? (
                    <h3 className={styles.title}>{data?.title}</h3>
                  ) : (
                    <h2 className={styles.title}>{data?.title}</h2>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Link>
  );
};
